import React, { createContext, useContext, useReducer } from 'react';
import { INITIAL_STATE, XP_PER_LEVEL } from '../data/initialState';

const AppContext = createContext(null);

// Alltid beregn daglig budsjett fra inntekt - faste utgifter / dager igjen i måneden
function calcDailyBudget(income, expenses, totalSaved = 0, payDay = null) {
  if (!income || income <= 0) return 0;
  const now = new Date();
  let daysLeft;
  if (payDay && payDay >= 1 && payDay <= 31) {
    // Calculate days until next payday
    const today = now.getDate();
    if (today <= payDay) {
      daysLeft = payDay - today;
    } else {
      // Next month's payday
      const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
      daysLeft = (daysInMonth - today) + payDay;
    }
    daysLeft = Math.max(1, daysLeft);
  } else {
    // No payDay set → use full month (30 days)
    daysLeft = 30;
  }
  const available = Math.max(0, income - (expenses || 0));
  return Math.round(available / daysLeft);
}

function checkAchievements(state, newState) {
  const updated = [...(newState.achievements || [])];
  let xpBonus = 0;

  // STRICT: only unlock if achievement was NOT unlocked before
  const newlyUnlocked = [];
  const unlock = (id) => {
    const idx = updated.findIndex(a => a.id === id);
    const wasUnlocked = (state.achievements || []).find(a => a.id === id)?.unlocked;
    if (idx !== -1 && !updated[idx].unlocked && !wasUnlocked) {
      updated[idx] = { ...updated[idx], unlocked: true };
      xpBonus += updated[idx].xp || 0;
      newlyUnlocked.push(id);
    }
  };

  const tx = (newState.transactions || []).filter(t => !t.locked);
  const prevTx = (state.transactions || []).filter(t => !t.locked);
  const goals = newState.goals || [];
  const prevGoals = state.goals || [];

  // Detect what JUST happened
  const addedTx = tx.length > prevTx.length;
  const addedGoal = goals.length > prevGoals.length;
  const savedToGoal = (newState.balance || 0) > (state.balance || 0);
  const streakIncreased = newState.streak > state.streak;

  // FREE achievements (1-5)
  // 1: First saving - only when user just added a saving transaction
  if (addedTx) {
    const savingTx = tx.filter(t => t.cat === 'Sparing' && t.amount > 0);
    if (savingTx.length > 0) unlock(1);
    // 5: 10 expense transactions
    const expenseTx = tx.filter(t => t.amount < 0);
    if (expenseTx.length >= 10) unlock(5);
  }
  // 2: 3-day streak - only when streak just reached 3
  if (streakIncreased && newState.streak >= 3) unlock(2);
  // 3: Saved 500 - only when balance just crossed 500
  if (savedToGoal && newState.balance >= 500 && state.balance < 500) unlock(3);
  // 4: First goal - only when user just added a goal with target
  if (addedGoal && goals.some(g => g.target > 0)) unlock(4);

  // ADVANCED (6-15)
  const isAdv = newState.plan === 'advanced' || newState.plan === 'premium';
  const isPrem = newState.plan === 'premium';
  if (isAdv) {
    if (streakIncreased && newState.streak >= 7) unlock(6);
    if (savedToGoal && newState.balance >= 1000 && state.balance < 1000) unlock(7);
    if (streakIncreased && newState.streak >= 7) unlock(8);
    if (savedToGoal && goals.some(g => g.saved >= g.target && prevGoals.find(pg => pg.id === g.id)?.saved < g.target)) unlock(9);
    if ((newState.referralCount || 0) > (state.referralCount || 0)) unlock(10);
    if (savedToGoal && newState.balance >= 5000 && state.balance < 5000) unlock(11);
    if (streakIncreased && newState.streak >= 30) unlock(12);
    if (streakIncreased && newState.streak >= 7) unlock(13);
  }

  // PREMIUM (16-30)
  if (isPrem) {
    if (savedToGoal && newState.balance >= 10000 && state.balance < 10000) unlock(16);
    if (savedToGoal && newState.balance >= 50000 && state.balance < 50000) unlock(17);
    if ((newState.referralCount || 0) >= 10 && (state.referralCount || 0) < 10) unlock(18);
    if (streakIncreased && newState.streak >= 100) unlock(20);
    if (savedToGoal && goals.filter(g => g.saved >= g.target).length >= 5) unlock(21);
    if (savedToGoal && newState.balance >= 100000 && state.balance < 100000) unlock(22);
    if (addedGoal && goals.length >= 10) unlock(29);
    if (savedToGoal && newState.balance >= 1000000 && state.balance < 1000000) unlock(30);
  }

  return { achievements: updated, xpBonus, newlyUnlocked };
}

function reducer(state, action) {
  switch (action.type) {

    case 'SET_USER':
      return { ...state, user: { ...state.user, ...action.payload } };
    case 'LOGIN': {
      const newState = { ...state, user: action.payload, theme: action.payload.theme || state.theme,
        username: action.payload.username || action.payload.name || state.username || '',
        avatar: action.payload.avatar_emoji || state.avatar || '🌱',
        referralCode: action.payload.referralCode || state.referralCode || '',
        referralCount: action.payload.referralCount || state.referralCount || 0,
        ambassador: action.payload.ambassador || state.ambassador || false,
      };
      // achievements checked only on specific actions
      return { ...newState };
    }

    case 'LOGOUT':
      return { ...INITIAL_STATE, user: null, lastActive: null, xpAnim: 0, darkMode: false, theme: 'sapphire' };

    case 'SET_PLAN':
      return { ...state, plan: action.payload };
    case 'SET_PAY_DAY':
      return { ...state, payDay: action.payload };
    case 'SET_LANGUAGE':
      try { localStorage.setItem('savely_lang', action.payload); sessionStorage.setItem('savely_lang', action.payload); } catch(e) {}
      return { ...state, language: action.payload };
    case 'SET_NAME':
      return { ...state, user: { ...state.user, name: action.payload } };
    case 'SET_DAILY_CHALLENGE':
      return { ...state, dailyChallenge: action.payload, dailyChallengeDate: new Date().toDateString(), dailyChallengeCompleted: false };
    case 'COMPLETE_DAILY_CHALLENGE':
      return { ...state, dailyChallengeCompleted: true, xp: state.xp + (action.payload || 25), xpAnim: action.payload || 25 };
    case 'SET_MILESTONE':
      return { ...state, milestoneReached: action.payload };
    case 'CLEAR_MILESTONE':
      return { ...state, milestoneReached: null };
    case 'SET_FRIENDS_SAVING':
      return { ...state, friendsSavingToday: action.payload };

    case 'SET_INCOME': {
      const { income, expenses } = action.payload;
      const newDailyBudget = calcDailyBudget(income, expenses, 0, state.payDay);
      const fixedTx = expenses > 0 ? [{
        id: 'fixed-expenses',
        desc: 'fixed_expenses_key',
        cat: 'Annet',
        amount: -expenses,
        date: 'Locked',
        time: '',
        locked: true,
      }] : [];
      const otherTx = (state.transactions || []).filter(t => t.id !== 'fixed-expenses');
      return { ...state, income, expenses, payDay: effectivePayDay || state.payDay, dailyBudget: newDailyBudget, transactions: [...fixedTx, ...otherTx] };
    }

    case 'UPDATE_INCOME': {
      const { income, expenses, payDay: newPayDay } = action.payload;
      const effectivePayDay = newPayDay || state.payDay;
      const newDailyBudget = calcDailyBudget(income, expenses, 0, effectivePayDay);
      const fixedTx = expenses > 0 ? [{
        id: 'fixed-expenses',
        desc: 'fixed_expenses_key',
        cat: 'Annet',
        amount: -expenses,
        date: 'Locked',
        time: '',
        locked: true,
      }] : [];
      const otherTx = (state.transactions || []).filter(t => t.id !== 'fixed-expenses');
      return { ...state, income, expenses, payDay: effectivePayDay || state.payDay, dailyBudget: newDailyBudget, transactions: [...fixedTx, ...otherTx] };
    }

    case 'ADD_TRANSACTION': {
      const tx = action.payload;
      const isSaving = tx.cat === 'Sparing';
      const todayStr = new Date().toDateString();
      const isToday = tx.date === 'I dag' || tx.date === 'Today' || tx.date === 'Idag' || tx.date === todayStr;
      const affectsDaily = isToday && tx.amount < 0 && !tx.fromBudget && !tx.locked;
      const newDailySpent = affectsDaily
        ? state.dailySpent + Math.abs(tx.amount)
        : state.dailySpent;
      const newBalance = state.balance;
      const today = new Date().toDateString();
      const yesterday = new Date(Date.now() - 86400000).toDateString();
      // Streak: increment when saving today, keep if already active today
      const hasSavedToday = isSaving;
      const newStreak = hasSavedToday
        ? (state.lastActive === today ? state.streak : state.lastActive === yesterday ? state.streak + 1 : 1)
        : (state.lastActive === yesterday ? state.streak + 1 : state.lastActive === today ? state.streak : 0);
      const baseXp = isSaving ? 0 : 10; // 10 XP for logging expenses

      // Hvis sparetransaksjon og målet ER fullført — sett goalCompleted flagg
      let finalTx = tx;
      if (isSaving && !tx.goalCompleted) {
        const relatedGoal = (state.goals || []).find(g => tx.desc && tx.desc.includes(g.name));
        if (relatedGoal && relatedGoal.saved >= relatedGoal.target) {
          finalTx = { ...tx, goalCompleted: true };
        }
      }

      const newTransactions = [finalTx, ...state.transactions];
      // Recalculate daily budget: (income - fixed expenses - spent today) / days left
      const subTotal = (state.subscriptions || []).reduce((s, sub) => s + (sub.amount || 0), 0);
      const newDailyBudget = calcDailyBudget(state.income, (state.expenses || 0) + subTotal, 0, state.payDay);
      const adjustedDailyBudget = affectsDaily
        ? Math.max(0, newDailyBudget - Math.round(newDailySpent / Math.max(1, (() => {
            const now = new Date(); const pd = state.payDay;
            if (pd && pd >= 1 && pd <= 31) {
              const d = pd - now.getDate();
              return Math.max(1, d > 0 ? d : (new Date(now.getFullYear(), now.getMonth()+1, 0).getDate() - now.getDate()) + pd);
            } return 30;
          })())))
        : newDailyBudget;
      const newState = { ...state, transactions: newTransactions, dailySpent: newDailySpent, dailyBudget: adjustedDailyBudget, balance: newBalance, streak: newStreak, lastActive: today, xp: state.xp + baseXp, level: Math.floor((state.xp + baseXp) / XP_PER_LEVEL) + 1 };
      const { achievements, xpBonus, newlyUnlocked } = checkAchievements(state, newState);
      const totalXp = newState.xp + xpBonus;
      return { ...newState, achievements, xp: totalXp, level: Math.floor(totalXp / XP_PER_LEVEL) + 1, xpAnim: baseXp + xpBonus, newlyUnlocked: newlyUnlocked || [] };
    }

    case 'DELETE_TRANSACTION': {
      const tx = (state.transactions || []).find(t => t.id === action.payload);
      if (!tx || tx.locked) return state;

      const isSaving = tx.cat === 'Sparing';
      const todayStr2 = new Date().toDateString();
      const wasToday = tx.date === 'I dag' || tx.date === 'Today' || tx.date === 'Idag' || tx.date === todayStr2;

      // Blokkér sletting av fullførte mål-transaksjoner
      // Stol KUN på goalCompleted-flagget — ikke søk i state.goals
      // (målet kan være slettet, men flagget sitter permanent på transaksjonen)
      if (isSaving && tx.goalCompleted === true) return state;

      // Finn relatert mål for å redusere goal.saved ved sletting
      const relatedGoal = isSaving
        ? (state.goals || []).find(g => tx.desc && tx.desc.includes(g.name))
        : null;

      // Fjern transaksjonen
      const newTransactions = (state.transactions || []).filter(t => t.id !== action.payload);

      // Gjenopprett dailySpent
      const newDailySpent = wasToday && tx.amount < 0 && !tx.fromBudget
        ? Math.max(0, state.dailySpent - Math.abs(tx.amount))
        : state.dailySpent;

      // Gjenopprett goal.saved og balance
      let newBalance = state.balance;
      let newGoals = state.goals;
      if (isSaving && relatedGoal) {
        const amt = Math.abs(tx.amount);
        // Reduser goal.saved
        newGoals = (state.goals || []).map(g =>
          g.id === relatedGoal.id
            ? { ...g, saved: Math.max(0, g.saved - amt) }
            : g
        );
        // Reduser balance tilsvarende
        newBalance = Math.max(0, state.balance - amt);
      } else if (isSaving && !relatedGoal) {
        // Sparetransaksjon uten relatert mål — tilbakestill balance
        newBalance = Math.max(0, state.balance - Math.abs(tx.amount));
      }

      // Daglig budsjett tilbakestilles basert på ny balance etter sletting
      const newDailyBudget = calcDailyBudget(state.income, state.expenses, 0, state.payDay);

      return { ...state, transactions: newTransactions, dailySpent: newDailySpent, balance: newBalance, goals: newGoals, dailyBudget: newDailyBudget };
    }

    // Forsiden: oppdater goal + balance, dailySpent via ADD_TRANSACTION
    case 'SAVE_TO_GOAL': {
      const { goalId, amount } = action.payload;
      const goal = (state.goals || []).find(g => g.id === goalId);
      const newSaved = goal ? Math.min(goal.target, goal.saved + amount) : amount;
      const isNowComplete = goal && newSaved >= goal.target;
      const newGoals = (state.goals || []).map(g =>
        g.id === goalId ? { ...g, saved: newSaved } : g
      );
      // Merk ALLE sparetransaksjoner for dette målet som fullført (inkl. fremtidige)
      const markComplete = (txList) => txList.map(t =>
        t.cat === 'Sparing' && t.desc && goal && t.desc.includes(goal.name)
          ? { ...t, goalCompleted: true }
          : t
      );
      const newTransactions = isNowComplete ? markComplete(state.transactions) : state.transactions;
      const newBalance = state.balance + amount;
      const newXp = state.xp + Math.round(amount / 10);
      const newState = { ...state, goals: newGoals, balance: newBalance, xp: newXp, transactions: newTransactions, level: Math.floor(newXp / XP_PER_LEVEL) + 1, xpAnim: Math.round(amount / 10) };
      const { achievements, xpBonus, newlyUnlocked } = checkAchievements(state, newState);
      return { ...newState, achievements, xp: newState.xp + xpBonus, newlyUnlocked: newlyUnlocked || [] };
    }

    // Budsjett: fordel over resterende dager, ikke trekk fra dailySpent
    case 'SAVE_TO_GOAL_BUDGET': {
      const { goalId, amount } = action.payload;
      const goal = (state.goals || []).find(g => g.id === goalId);
      const newSaved = goal ? Math.min(goal.target, goal.saved + amount) : amount;
      const isNowComplete = goal && newSaved >= goal.target;
      const newGoals = (state.goals || []).map(g =>
        g.id === goalId ? { ...g, saved: newSaved } : g
      );
      const markCompleteBudget = (txList) => txList.map(t =>
        t.cat === 'Sparing' && t.desc && goal && t.desc.includes(goal.name)
          ? { ...t, goalCompleted: true }
          : t
      );
      const newTransactions = isNowComplete ? markCompleteBudget(state.transactions) : state.transactions;
      const newBalance = state.balance + amount;
      const newXp = state.xp + Math.round(amount / 10);
      // Daglig budsjett = inntekt - faste utgifter / dager igjen (uendret av sparing)
      const newDailyBudget = calcDailyBudget(state.income, state.expenses, 0, state.payDay);
      const newState = { ...state, goals: newGoals, balance: newBalance, xp: newXp, transactions: newTransactions, level: Math.floor(newXp / XP_PER_LEVEL) + 1, xpAnim: Math.round(amount / 10), dailyBudget: newDailyBudget };
      const { achievements, xpBonus } = checkAchievements(state, newState);
      return { ...newState, achievements, xp: newState.xp + xpBonus };
    }

    case 'ADD_GOAL': {
      const newGoals = [...state.goals, action.payload];
      const newState = { ...state, goals: newGoals };
      const { achievements, xpBonus, newlyUnlocked } = checkAchievements(state, newState);
      return { ...newState, achievements, xp: newState.xp + xpBonus, xpAnim: xpBonus > 0 ? xpBonus : 0, newlyUnlocked: newlyUnlocked || [] };
    }

    case 'EDIT_GOAL':
      return { ...state, goals: (state.goals || []).map(g => g.id === action.payload.id ? { ...g, ...action.payload } : g) };

    case 'UPDATE_GOAL': {
      const updated = (state.goals || []).map(g => g.id === action.payload.id ? action.payload : g);
      return { ...state, goals: updated };
    }

    case 'DELETE_GOAL': {
      const goal = (state.goals || []).find(g => g.id === action.payload);
      const newGoals = (state.goals || []).filter(g => g.id !== action.payload);
      const goalName = goal?.name || '';
      const isCompleted = goal && goal.saved >= goal.target;

      // Fullført mål: fjern kun fra listen, ingenting annet
      if (isCompleted) {
        return { ...state, goals: newGoals };
      }

      // Uferdig mål: tilbakestill alt - gi penger tilbake
      const newTransactions = (state.transactions || []).filter(t =>
        !(t.cat === 'Sparing' && t.desc && t.desc.includes(goalName))
      );
      const savedAmount = goal ? goal.saved : 0;
      // Return money to user - add back what was saved to this goal
      const newBalance = state.balance + savedAmount;
      // Daglig budsjett tilbakestilles basert på ny balance
      const newDailyBudget = calcDailyBudget(state.income, state.expenses, 0, state.payDay);
      const removedToday = (state.transactions || []).filter(t =>
        t.cat === 'Sparing' && t.desc && t.desc.includes(goalName) && t.date === 'I dag' && !t.fromBudget
      ).reduce((s, t) => s + Math.abs(t.amount), 0);
      const newDailySpent = Math.max(0, state.dailySpent - removedToday);
      return { ...state, goals: newGoals, balance: newBalance, dailyBudget: newDailyBudget, dailySpent: newDailySpent, transactions: newTransactions };
    }

    case 'RESET_DAILY': {
      const freshBudget = calcDailyBudget(state.income, state.expenses, 0, state.payDay);
      const today = new Date().toDateString();
      const yesterday = new Date(Date.now() - 86400000).toDateString();
      // Streak: fortsett hvis spart i går, nullstill hvis ikke
      const hasSavedYesterday = (state.transactions || []).some(t => 
        t.cat === 'Sparing' && new Date(t.date).toDateString() === yesterday
      );
      const newStreak = hasSavedYesterday ? state.streak : 0;
      return { ...state, dailySpent: 0, lastResetDate: today, dailyBudget: freshBudget || state.dailyBudget, streak: newStreak };
    }

    case 'SET_PAYDAY':
      return { ...state, payDay: action.payload };

    case 'LOAD_PROFILE': {
      const p = action.payload;
      // Beregn alltid daglig budsjett på nytt basert på dager igjen
      // Don't overwrite existing income/expenses with 0 from DB
      const income = p.income > 0 ? p.income : (state.income || 0);
      const expenses = p.expenses > 0 ? p.expenses : (state.expenses || 0);
      const balance = p.balance > 0 ? p.balance : (state.balance || 0);
      const freshDailyBudget = calcDailyBudget(income, expenses, 0, p.pay_day ? parseInt(p.pay_day) : null);
      return { ...state, xpAnim: 0,
        username: p.username || p.name || state.username || '',
        avatar: p.avatar_emoji || state.avatar || '🌱',
        displayName: p.display_name || p.username || state.username || '',
        referralCode: p.referral_code || state.referralCode || '',
        referralCount: p.referral_count || state.referralCount || 0,
        plan: p.plan || state.plan,
        theme: p.theme || state.theme,
        darkMode: p.dark_mode !== undefined ? p.dark_mode : state.darkMode,
        income,
        expenses,
        dailyBudget: freshDailyBudget > 0 ? freshDailyBudget : (p.daily_budget || 0),
        dailySpent: p.daily_spent || 0,
        balance,
        xp: p.xp || 0,
        level: p.level || 1,
        streak: p.streak || 0,
        lastActive: p.last_active || null,
        payDay: p.pay_day || null,
        lastResetDate: p.last_reset_date || null,
        language: p.language || state.language || 'en',
        chatCount: (() => {
          const today = new Date().toDateString();
          if (p.chat_date === today) {
            try { localStorage.setItem('savely_chat_count', JSON.stringify({date:today,count:p.chat_count||0})); } catch(e){}
            return p.chat_count || 0;
          }
          return 0;
        })(),
        user: state.user ? {
          ...state.user,
          username: p.username || state.user?.username || '',
          name: p.name || state.user?.name || '',
          avatar_emoji: p.avatar_emoji || state.user?.avatar_emoji || state.avatar || '🌱',
        } : state.user,
      };
    }

    case 'LOAD_TRANSACTIONS':
      return { ...state, transactions: action.payload };

    case 'LOAD_GOALS':
      return { ...state, goals: action.payload };

    case 'LOAD_SUBSCRIPTIONS':
      return { ...state, subscriptions: action.payload };

    case 'LOAD_ACHIEVEMENTS': {
      const loaded = action.payload;
      // Only show unlocked if user has real activity
      const hasRealActivity = (state.xp || 0) > 0 || 
        (state.transactions || []).filter(t => !t.locked).length > 0 ||
        (state.streak || 0) > 0;
      const updated = (state.achievements || []).map(a => {
        const found = loaded.find(l => l.id === a.id);
        // Never show as unlocked if user has no real activity
        if (!hasRealActivity) return { ...a, unlocked: false };
        return { ...a, unlocked: found?.unlocked === true };
      });
      return { ...state, achievements: updated };
    }

    case 'ADD_XP': {
      const newXp = state.xp + action.payload;
      return { ...state, xp: newXp, level: Math.floor(newXp / XP_PER_LEVEL) + 1, xpAnim: action.payload };
    }

    case 'CLEAR_XP_ANIM':
      return { ...state, xpAnim: 0 };

    case 'ADD_SUBSCRIPTION': {
      const newSubs = [...(state.subscriptions || []), action.payload];
      // Spread subscription cost over remaining days
      const subTotal = newSubs.reduce((s, sub) => s + (sub.amount || 0), 0);
      const newDailyBudget = calcDailyBudget(state.income || 0, (state.expenses || 0) + subTotal, 0, state.payDay);
      return { ...state, subscriptions: newSubs, dailyBudget: newDailyBudget > 0 ? newDailyBudget : state.dailyBudget };
    }

    case 'DELETE_SUBSCRIPTION': {
      const filtered = (state.subscriptions || []).filter(s => s.id !== action.payload);
      const removed = (state.subscriptions || []).find(s => s.id === action.payload);
      const newExpenses2 = Math.max(0, (state.expenses || 0) - (removed?.amount || 0));
      const newDailyBudget2 = calcDailyBudget(state.income || 0, newExpenses2, 0, state.payDay);
      return { ...state, subscriptions: filtered, dailyBudget: newDailyBudget2 > 0 ? newDailyBudget2 : state.dailyBudget };
    }

    case 'UPDATE_USER': {
      return { ...state,
        user: { ...state.user, ...action.payload },
        username: action.payload.username || state.username,
        avatar: action.payload.avatar_emoji || state.avatar,
      };
    }

    case 'SET_THEME':
      return { ...state, theme: action.payload };

    case 'CLEAR_NEWLY_UNLOCKED':
      return { ...state, newlyUnlocked: [] };

    case 'TOGGLE_DARK_MODE':
      return { ...state, darkMode: !state.darkMode };

    default:
      return state;
  }
}

export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, {
    ...INITIAL_STATE,
    lastActive: null,
    xpAnim: 0,
  dailyChallenge: null,
  dailyChallengeCompleted: false,
  dailyChallengeDate: null,
  milestoneReached: null,
  friendsSavingToday: 0,
    darkMode: false,
    theme: 'sapphire',
  });
  return <AppContext.Provider value={{ state, dispatch }}>{children}</AppContext.Provider>;
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) return { state: {}, dispatch: () => {} };
  return context;
}
